// Core Admin Types
export interface AdminUser {
  id: string
  username: string
  email: string
  role: AdminRole
  mfa_enabled: boolean
  last_login?: string
  created_at: string
}

export enum AdminRole {
  SUPER_ADMIN = 'super_admin',
  OPERATIONS_MANAGER = 'operations_manager',
  SUPPORT_AGENT = 'support_agent',
  READ_ONLY = 'read_only'
}

export interface AdminSession {
  token: string
  expires_at: string
  user: AdminUser
}

// User Management Types
export interface User {
  id: string
  email: string
  first_name?: string
  last_name?: string
  name?: string
  company?: string
  phone?: string
  user_type: string
  is_active: boolean
  is_verified: boolean
  terms_accepted: boolean
  onboarding_completed: boolean
  agency_id?: string
  agency_role?: string
  is_agency_owner: boolean
  watermark_settings: WatermarkSettings
  credits: number
  created_at: string
  updated_at?: string
}

export interface WatermarkSettings {
  type: 'none' | 'logo' | 'text' | 'both'
  logo_config: {
    url?: string
    position: string
    size: string
    opacity: number
    margin: number
    custom_percentage?: number
  }
  text_config: {
    content: string
    position: string
    font_size: number
    color: string
    opacity: number
    font_family: string
    margin: number
    background_color?: string
    background_opacity: number
  }
  template?: string
  last_used_logos: string[]
  preferences: {
    remember_settings: boolean
    apply_to_mls: boolean
    apply_to_organizer: boolean
    apply_to_simple: boolean
  }
}

export interface CreditTransaction {
  id: string
  user_id: string
  amount: number
  balance_after: number
  transaction_type: string
  description?: string
  payment_intent_id?: string
  created_at: string
}

// Generation Types
export interface Generation {
  id: string
  user_id: string
  task_id?: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  original_image_url?: string
  generated_image_url?: string
  original_thumbnail_url?: string
  transformed_thumbnail_url?: string
  style?: string
  room_type?: string
  lighting?: string
  quality: 'standard' | 'high' | 'ultra'
  property_name?: string
  processing_time_seconds?: number
  credits_used: number
  error_message?: string
  generation_type: 'standard' | 'mixed_style' | 'batch'
  generation_metadata?: any
  property_id?: string
  created_at: string
  completed_at?: string
}

// MLS Types
export interface MLSProvider {
  id: string
  name: string
  provider_type: string
  short_code: string
  min_width: number
  min_height: number
  recommended_width: number
  recommended_height: number
  max_file_size_kb: number
  required_format: string
  config?: any
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface MLSExportBatch {
  id: string
  user_id: string
  provider_id: string
  property_id?: string
  batch_name?: string
  export_parameters?: any
  image_ids: string[]
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'completed_with_errors'
  progress_info?: {
    total: number
    processed: number
    failed: number
  }
  download_url?: string
  zip_file_path?: string
  celery_task_id?: string
  created_at: string
  updated_at: string
  started_at?: string
  completed_at?: string
}

// Property Types
export interface Property {
  id: string
  user_id: string
  name: string
  address?: string
  description?: string
  property_type?: string
  status: string
  metadata?: any
  created_at: string
  updated_at: string
}

// Analytics Types
export interface SystemHealth {
  api_status: 'healthy' | 'degraded' | 'down'
  database_status: 'connected' | 'slow' | 'disconnected'
  redis_status: 'connected' | 'disconnected'
  celery_workers: {
    active: number
    total: number
    queues: QueueStatus[]
  }
  generation_metrics: {
    pending: number
    processing: number
    completed_today: number
    failed_today: number
    avg_processing_time: number
  }
  mls_exports: {
    pending: number
    processing: number
    completed_today: number
    failed_today: number
  }
}

export interface QueueStatus {
  name: string
  pending: number
  active: number
  failed: number
  priority: number
}

export interface DashboardStats {
  total_users: number
  active_users: number
  total_credits_issued: number
  total_generations: number
  users_registered_today: number
  generations_today: number
  revenue_today: number
  revenue_month: number
  mls_exports_today: number
  avg_generation_time: number
  system_uptime: number
  error_rate: number
}

// Financial Types
export interface RevenueMetrics {
  daily_revenue: number
  monthly_revenue: number
  yearly_revenue: number
  credit_sales: CreditSale[]
  payment_failures: PaymentFailure[]
  growth_rate: number
}

export interface CreditSale {
  id: string
  user_id: string
  amount: number
  credits: number
  payment_intent_id: string
  status: string
  created_at: string
}

export interface PaymentFailure {
  id: string
  user_id: string
  amount: number
  error_code: string
  error_message: string
  created_at: string
}

// Worker Monitoring Types
export interface WorkerStatus {
  id: string
  hostname: string
  status: 'online' | 'offline' | 'busy'
  active_tasks: number
  processed_tasks: number
  failed_tasks: number
  load_average: number[]
  memory_usage: number
  last_heartbeat: string
}

// Audit Types
export interface AuditLog {
  id: string
  admin_user_id: string
  admin_username: string
  action_type: string
  resource_type?: string
  resource_id?: string
  details: any
  ip_address: string
  user_agent?: string
  created_at: string
}

// API Response Types
export interface ApiResponse<T> {
  data: T
  message?: string
  success: boolean
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  per_page: number
  total_pages: number
}

// Form Types
export interface LoginForm {
  username: string
  password: string
  mfa_code?: string
}

export interface UserUpdateForm {
  first_name?: string
  last_name?: string
  email?: string
  is_active?: boolean
  user_type?: string
}

export interface CreditAdjustmentForm {
  amount: number
  reason: string
  admin_note?: string
}

// Filter and Search Types
export interface UserFilters {
  search?: string
  user_type?: string
  is_active?: boolean
  created_after?: string
  created_before?: string
  has_generations?: boolean
  min_credits?: number
  max_credits?: number
}

export interface GenerationFilters {
  user_id?: string
  status?: string
  style?: string
  room_type?: string
  quality?: string
  created_after?: string
  created_before?: string
  has_errors?: boolean
}

export interface MLSFilters {
  provider_id?: string
  status?: string
  created_after?: string
  created_before?: string
  user_id?: string
}

// Chart Data Types
export interface ChartDataPoint {
  date: string
  value: number
  label?: string
}

export interface TimeSeriesData {
  label: string
  data: ChartDataPoint[]
  color?: string
}

// Notification Types
export interface NotificationConfig {
  email_alerts: boolean
  slack_webhook?: string
  critical_threshold: number
  warning_threshold: number
}

// Admin Notification Types
export interface AdminNotificationResponse {
  id: string
  admin_user_id?: string
  notification_type: string
  title: string
  message: string
  severity: 'info' | 'warning' | 'error' | 'critical'
  is_read: boolean
  notification_metadata?: Record<string, any>
  created_at: string
  read_at?: string
}

export interface AdminNotificationCreate {
  notification_type: string
  title: string
  message: string
  severity?: 'info' | 'warning' | 'error' | 'critical'
  admin_user_id?: string
  notification_metadata?: Record<string, any>
}

export interface PaginatedNotificationsResponse {
  data: AdminNotificationResponse[]
  total: number
  page: number
  per_page: number
  total_pages: number
  unread_count: number
}

// Feature Flag Types
export interface FeatureFlag {
  name: string
  enabled: boolean
  description: string
  rollout_percentage: number
  conditions?: any
}

// Configuration Types
export interface SystemConfig {
  maintenance_mode: boolean
  rate_limits: RateLimitConfig[]
  feature_flags: FeatureFlag[]
  notification_config: NotificationConfig
}

export interface RateLimitConfig {
  endpoint: string
  limit: number
  window: string
  enabled: boolean
}

// Workflow Management Types
export enum QualityTier {
  TESTING = 'testing',
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  EXTREME = 'extreme',
  UPSCALE = 'upscale'
}

export enum WorkflowStatus {
  DRAFT = 'draft',
  TESTING = 'testing',
  ACTIVE = 'active',
  DEPRECATED = 'deprecated',
  FAILED = 'failed'
}

export interface WorkflowVersion {
  id: string
  quality_tier: string
  version: number
  name: string
  description: string
  credits: number
  processing_time_estimate: string
  status: string
  checksum: string
  created_at: string
  activated_at?: string
  tested_at?: string
  created_by?: string
  has_test_results: boolean
}

export interface QualityTierOverview {
  quality_tier: string
  active_workflow?: WorkflowVersion
  total_versions: number
  latest_version: number
  credits: number
  processing_time: string
}

export interface WorkflowTestResult {
  workflow_id: string
  test_image_path: string
  success: boolean
  execution_time: number
  output_image_path?: string
  error_message?: string
  tested_at: string
}

export interface WorkflowUploadRequest {
  quality_tier: string
  name: string
  description: string
  workflow_json: Record<string, any>
}

export interface WorkflowDetails {
  workflow: WorkflowVersion
  workflow_json: Record<string, any>
  test_results?: WorkflowTestResult
}

export interface PlaceholderInfo {
  key: string
  description: string
  example_value: string
  required: boolean
}